import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Navbar, Footer } from "@/components/Layout";
import { BeforeAfterSlider } from "@/components/BeforeAfterSlider";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MagnifyingGlassIcon, CheckCircleIcon, CircleIcon, CalendarCheckIcon, CurrencyDollarIcon, FilePdfIcon, ReceiptIcon, StarIcon, CreditCardIcon, FileTextIcon, DownloadSimpleIcon, XIcon, CaretLeftIcon, CaretRightIcon, MagnifyingGlassPlusIcon, ArrowsHorizontalIcon } from "@phosphor-icons/react";

const TIMELINE = [
  { key: "new", label: "Received", desc: "We got your request" },
  { key: "scheduled", label: "Scheduled", desc: "A visit is booked" },
  { key: "in-progress", label: "In Progress", desc: "Work underway" },
  { key: "completed", label: "Completed", desc: "Job finished" },
];

const STATUS_STYLE = {
  new: "bg-slate-200 text-slate-800",
  scheduled: "bg-gold-100 text-gold-800",
  "in-progress": "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
};

export default function TrackRequest() {
  const location = useLocation();
  const [ref, setRef] = useState(location.state?.ref || "");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { photos:[], index }

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.photos.length });
      if (e.key === "ArrowLeft") setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const search = async (e, override) => {
    e?.preventDefault();
    const r = (override?.ref ?? ref).trim();
    const em = (override?.email ?? email).trim();
    if (!r || !em) {
      toast.error("Enter your reference ID and email.");
      return;
    }
    setLoading(true);
    setData(null);
    try {
      const res = await api.get("/requests/track", { params: { ref: r, email: em } });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const currentIdx = data ? TIMELINE.findIndex((t) => t.key === data.status) : -1;

  const [quoteLoading, setQuoteLoading] = useState(false);
  const respondQuote = async (decision) => {
    setQuoteLoading(true);
    try {
      const res = await api.post("/requests/quote-response", { ref: data.ref, email, decision });
      setData(res.data);
      toast.success(decision === "accept" ? "Quote accepted!" : "Quote declined");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setQuoteLoading(false);
    }
  };

  const [payLoading, setPayLoading] = useState(false);
  const payNow = async (amount_type) => {
    setPayLoading(true);
    try {
      const res = await api.post("/payments/checkout", { ref: data.ref, email, amount_type, origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Payment could not be started");
      setPayLoading(false);
    }
  };

  const [rating, setRating] = useState(0);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewLoading, setReviewLoading] = useState(false);
  const submitReview = async () => {
    setReviewLoading(true);
    try {
      const res = await api.post("/requests/review", { ref: data.ref, email, rating, comment: reviewComment });
      setData(res.data);
      toast.success("Thanks for your review!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setReviewLoading(false);
    }
  };

  const [agr, setAgr] = useState(null);
  const [agAgree, setAgAgree] = useState(false);
  const [agName, setAgName] = useState("");
  const [agLoading, setAgLoading] = useState(false);
  useEffect(() => {
    if (!data?.ref || !email) return;
    api.get(`/requests/${data.ref}/agreement`, { params: { email } })
      .then((r) => { setAgr(r.data); setAgName((n) => n || data.name || ""); })
      .catch(() => {});
  }, [data?.ref]); // eslint-disable-line
  const signAgreement = async () => {
    if (!agAgree) { toast.error("Please check the box to agree"); return; }
    if (!agName.trim()) { toast.error("Type your full name to sign"); return; }
    setAgLoading(true);
    try {
      const res = await api.post("/requests/agreement-sign", { ref: data.ref, email, signature_name: agName });
      setData(res.data);
      const a = await api.get(`/requests/${data.ref}/agreement`, { params: { email } });
      setAgr(a.data);
      toast.success("Agreement signed! A copy has been emailed to you.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Sign failed");
    } finally {
      setAgLoading(false);
    }
  };

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    const qref = p.get("ref");
    const qemail = p.get("email");
    if (qref) setRef(qref);
    if (qemail) setEmail(qemail);
    if (qref && qemail) search(null, { ref: qref, email: qemail });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!data) return;
    const p = new URLSearchParams(location.search);
    if (p.get("review") === "1") {
      setTimeout(() => document.getElementById("review-section")?.scrollIntoView({ behavior: "smooth", block: "center" }), 300);
    }
  }, [data]); // eslint-disable-line

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto w-full px-6 py-14">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-gold-600 mb-3">Track Request</p>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-2">Check your status</h1>
        <p className="text-slate-600 mb-8">Enter the reference ID we gave you along with the email you used.</p>

        <form onSubmit={search} className="bg-white border border-slate-200 rounded-sm p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 items-end shadow-crisp">
          <div>
            <Label className="text-slate-900 font-medium mb-2 block">Reference ID</Label>
            <Input data-testid="track-ref-input" value={ref} onChange={(e) => setRef(e.target.value)} placeholder="HR-XXXXXXXX" className="font-mono bg-white border-slate-300" />
          </div>
          <div>
            <Label className="text-slate-900 font-medium mb-2 block">Email</Label>
            <Input data-testid="track-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" className="bg-white border-slate-300" />
          </div>
          <button
            type="submit"
            data-testid="track-search-btn"
            disabled={loading}
            className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-7 py-3 transition-colors active:scale-95 disabled:opacity-60"
          >
            <MagnifyingGlassIcon size={18} weight="bold" /> {loading ? "Searching…" : "Track"}
          </button>
        </form>

        {data && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            data-testid="track-result"
            className="mt-8 bg-white border border-slate-200 rounded-sm p-7"
          >
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
              <div>
                <p className="font-mono text-xl font-bold text-slate-900">{data.ref}</p>
                <p className="text-sm text-slate-500 capitalize">{data.property_type} · {data.categories.join(", ")}</p>
              </div>
              <span data-testid="track-status-badge" className={`px-3 py-1 rounded-sm text-xs font-bold uppercase tracking-wide ${STATUS_STYLE[data.status]}`}>
                {data.status}
              </span>
            </div>

            {agr && (
              <div className="mb-6 border-2 border-gold-200 bg-gold-50/40 rounded-sm p-5" data-testid="track-agreement">
                <div className="flex items-center gap-2 mb-1">
                  <FileTextIcon size={20} weight="fill" className="text-gold-600" />
                  <p className="font-heading font-semibold text-slate-900">Service Agreement &amp; Liability Waiver</p>
                </div>
                {data.agreement?.signed_name ? (
                  <div data-testid="agreement-signed">
                    <p className="text-sm text-green-700 font-semibold flex items-center gap-2 mt-1"><CheckCircleIcon size={18} weight="fill" /> Signed by {data.agreement.signed_name} on {data.agreement.signed_at?.slice(0, 10)}</p>
                    {data.agreement?.signed_ip && (
                      <p className="text-xs text-slate-400 mt-0.5" data-testid="agreement-proof">Verified · IP {data.agreement.signed_ip} · {data.agreement.signed_at?.slice(11, 16)} UTC</p>
                    )}
                    <a href={`${API}/requests/${data.ref}/agreement.pdf?email=${encodeURIComponent(email)}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-gold-700 hover:text-gold-800"><DownloadSimpleIcon size={16} /> Download signed copy</a>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-slate-600 mb-3">Please review and sign before work begins.{agr.governing_state ? ` Governed by the laws of ${agr.governing_state}.` : ""}</p>
                    <div className="max-h-44 overflow-y-auto bg-white border border-slate-200 rounded-sm p-3 text-xs text-slate-600 space-y-2" data-testid="agreement-scroll">
                      {agr.clauses.map((c) => (
                        <div key={c.heading}><span className="font-semibold text-slate-800">{c.heading}</span> — {c.body}</div>
                      ))}
                    </div>
                    <a href={`${API}/requests/${data.ref}/agreement.pdf?email=${encodeURIComponent(email)}`} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gold-700 hover:text-gold-800"><DownloadSimpleIcon size={14} /> Download full PDF</a>
                    <label className="flex items-start gap-2 mt-3 cursor-pointer">
                      <input type="checkbox" data-testid="agreement-agree" checked={agAgree} onChange={(e) => setAgAgree(e.target.checked)} className="mt-1" />
                      <span className="text-sm text-slate-700">I have read and agree to the Service Agreement &amp; Liability Waiver.</span>
                    </label>
                    <div className="mt-3 flex flex-col sm:flex-row gap-3">
                      <Input data-testid="agreement-signature" value={agName} onChange={(e) => setAgName(e.target.value)} placeholder="Type your full name to sign" className="bg-white border-slate-300 sm:max-w-xs" />
                      <button data-testid="agreement-sign-btn" disabled={agLoading} onClick={signAgreement} className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-6 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-60">{agLoading ? "Signing…" : "Sign Agreement"}</button>
                    </div>
                  </>
                )}
              </div>
            )}

            {data.scheduled_date && (
              <div data-testid="track-scheduled" className="mb-6 flex items-center gap-3 bg-gold-50 border border-gold-200 rounded-sm px-4 py-3">
                <CalendarCheckIcon size={22} weight="fill" className="text-gold-600" />
                <p className="text-sm text-slate-800">
                  Scheduled for <span className="font-semibold">{data.scheduled_date}</span>
                </p>
              </div>
            )}

            {data.reschedule_history && data.reschedule_history.length > 0 && (
              <div data-testid="track-reschedule-history" className="mb-6 bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
                <p className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Appointment changes</p>
                <ul className="space-y-1.5">
                  {[...data.reschedule_history].reverse().map((h, k) => (
                    <li key={k} data-testid={`track-reschedule-entry-${k}`} className="text-sm text-slate-600">
                      <span className="font-mono text-xs"><s className="text-slate-400">{h.from}</s> → <span className="font-semibold text-slate-800">{h.to}</span></span>
                      {h.reason && <span className="text-slate-500"> — “{h.reason}”</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {data.estimate && (
              <div data-testid="track-estimate" className="mb-6 bg-navy-900 rounded-sm px-5 py-4">
                <div className="flex items-center gap-3">
                  <CurrencyDollarIcon size={22} weight="fill" className="text-gold-500" />
                  <p className="text-sm text-white">
                    Estimated cost: <span className="font-semibold text-gold-400">{data.estimate}</span>
                  </p>
                </div>
                <a
                  data-testid="download-quote-pdf"
                  href={`${API}/requests/${encodeURIComponent(data.ref)}/quote.pdf?email=${encodeURIComponent(email)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-gold-400 hover:text-gold-300"
                >
                  <FilePdfIcon size={18} weight="fill" /> Download PDF quote
                </a>
                {data.quote_status === "accepted" && (
                  <p data-testid="quote-status" className="mt-3 text-sm font-semibold text-green-400 flex items-center gap-2">
                    <CheckCircleIcon size={18} weight="fill" /> You accepted this quote
                  </p>
                )}
                {data.quote_status === "declined" && (
                  <p data-testid="quote-status" className="mt-3 text-sm font-semibold text-red-400">You declined this quote</p>
                )}
                {(!data.quote_status || data.quote_status === "pending") && (
                  <div className="mt-4 flex gap-3">
                    <button
                      data-testid="quote-accept-btn"
                      disabled={quoteLoading}
                      onClick={() => respondQuote("accept")}
                      className="flex-1 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-4 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-60"
                    >
                      Accept Quote
                    </button>
                    <button
                      data-testid="quote-decline-btn"
                      disabled={quoteLoading}
                      onClick={() => respondQuote("decline")}
                      className="flex-1 rounded-sm border border-slate-600 hover:border-red-500 text-white font-semibold px-4 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-60"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="relative pl-2">
              {TIMELINE.map((t, i) => {
                const done = i <= currentIdx;
                const isCurrent = i === currentIdx;
                return (
                  <div key={t.key} className="flex gap-4 pb-8 last:pb-0 relative">
                    {i < TIMELINE.length - 1 && (
                      <span className={`absolute left-[11px] top-6 bottom-0 w-0.5 ${i < currentIdx ? "bg-gold-600" : "bg-slate-200"}`} />
                    )}
                    <span className="relative z-10 mt-0.5">
                      {done ? (
                        <CheckCircleIcon size={24} weight="fill" className={isCurrent ? "text-gold-600" : "text-green-600"} />
                      ) : (
                        <CircleIcon size={24} className="text-slate-300" />
                      )}
                    </span>
                    <div>
                      <p className={`font-semibold ${done ? "text-slate-900" : "text-slate-400"}`}>{t.label}</p>
                      <p className="text-sm text-slate-500">{t.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            {data.admin_note && (
              <div className="mt-4 bg-gold-50 border border-gold-200 rounded-sm p-4">
                <p className="text-xs uppercase tracking-wide font-bold text-gold-700 mb-1">Note from our team</p>
                <p className="text-sm text-slate-700">{data.admin_note}</p>
              </div>
            )}

            {data.photos && data.photos.length > 0 && (
              <div className="mt-5">
                <p className="text-xs uppercase tracking-wide font-bold text-slate-500 mb-2">Your photos</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {data.photos.map((p) => (
                    <img key={p} src={`${API}/files/${p}`} alt="attachment" className="w-full aspect-square object-cover rounded-sm border border-slate-200" />
                  ))}
                </div>
              </div>
            )}

            {data.photos && data.photos.length > 0 && data.completion_photos && data.completion_photos.length > 0 && (
              <div className="mt-5" data-testid="before-after-section">
                <p className="text-xs uppercase tracking-wide font-bold text-gold-700 mb-2 flex items-center gap-1.5">
                  <ArrowsHorizontalIcon size={14} weight="bold" /> Before &amp; After
                  <span className="text-slate-400 font-medium normal-case tracking-normal">· drag the slider to compare</span>
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {data.photos.slice(0, data.completion_photos.length).map((p, i) => (
                    <BeforeAfterSlider
                      key={`${p}-${i}`}
                      before={`${API}/files/${p}`}
                      after={`${API}/files/${data.completion_photos[i]}`}
                    />
                  ))}
                </div>
              </div>
            )}

            {data.completion_photos && data.completion_photos.length > 0 && (
              <div className="mt-5" data-testid="track-completion-photos">
                <p className="text-xs uppercase tracking-wide font-bold text-green-700 mb-2 flex items-center gap-1.5">
                  <CheckCircleIcon size={14} weight="fill" /> Finished work
                  <span className="text-slate-400 font-medium normal-case tracking-normal">· {data.completion_photos.length} photo{data.completion_photos.length > 1 ? "s" : ""} · tap to enlarge</span>
                </p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {data.completion_photos.map((p, i) => (
                    <button
                      key={p}
                      type="button"
                      data-testid={`gallery-thumb-${i}`}
                      onClick={() => setLightbox({ photos: data.completion_photos, index: i })}
                      className="group relative block w-full aspect-square rounded-sm overflow-hidden border border-green-200 hover:border-green-500 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      <img src={`${API}/files/${p}`} alt="finished work" className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
                      <span className="absolute inset-0 bg-navy-900/0 group-hover:bg-navy-900/30 transition-colors flex items-center justify-center">
                        <MagnifyingGlassPlusIcon size={22} weight="bold" className="text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {data.invoice && data.invoice.total > 0 && (
              <div className="mt-6 border border-slate-200 rounded-sm p-5" data-testid="track-invoice">
                <div className="flex items-center justify-between mb-3">
                  <p className="font-heading font-semibold text-slate-900 flex items-center gap-2"><ReceiptIcon size={18} className="text-gold-600" /> Invoice</p>
                  <span data-testid="invoice-status" className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase ${data.invoice.status === "paid" ? "bg-green-100 text-green-800" : data.invoice.status === "deposit_paid" ? "bg-gold-100 text-gold-800" : "bg-slate-200 text-slate-700"}`}>
                    {data.invoice.status.replace("_", " ")}
                  </span>
                </div>
                <div className="space-y-1 text-sm">
                  {data.invoice.line_items.map((it, i) => (
                    <div key={i} className="flex justify-between text-slate-600">
                      <span>{it.description} × {it.qty}</span>
                      <span>${(it.qty * it.unit_price).toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between border-t border-slate-200 mt-3 pt-3 font-semibold text-slate-900">
                  <span>Total</span>
                  <span>${data.invoice.total.toFixed(2)}</span>
                </div>
                {data.invoice.status !== "paid" ? (
                  <div className="mt-4 flex gap-3">
                    {data.invoice.status === "unpaid" && (
                      <button data-testid="pay-deposit-btn" disabled={payLoading} onClick={() => payNow("deposit")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm border border-slate-300 hover:border-gold-600 text-slate-900 font-semibold px-4 py-2.5 text-sm transition-colors disabled:opacity-60">
                        <CreditCardIcon size={16} /> Pay {Math.round(data.invoice.deposit_pct)}% deposit
                      </button>
                    )}
                    <button data-testid="pay-full-btn" disabled={payLoading} onClick={() => payNow("full")} className="flex-1 inline-flex items-center justify-center gap-2 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-4 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-60">
                      <CreditCardIcon size={16} /> {data.invoice.status === "deposit_paid" ? "Pay balance" : "Pay in full"}
                    </button>
                  </div>
                ) : (
                  <p className="mt-3 text-sm font-semibold text-green-600">Paid in full — thank you!</p>
                )}
                {data.invoice.status === "deposit_paid" && <p className="mt-2 text-xs text-slate-500">Deposit received. Balance due on completion.</p>}
              </div>
            )}

            {data.status === "completed" && (
              <div id="review-section" className="mt-6 border border-slate-200 rounded-sm p-5" data-testid="track-review">
                {data.review ? (
                  <>
                    <p className="font-heading font-semibold text-slate-900 mb-2">Your review</p>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <StarIcon key={n} size={22} weight={n <= data.review.rating ? "fill" : "regular"} className="text-gold-500" />
                      ))}
                    </div>
                    {data.review.comment && <p className="mt-2 text-sm text-slate-600">{data.review.comment}</p>}
                    {data.review.reply && (
                      <div className="mt-3 border-l-2 border-gold-500 pl-3" data-testid="track-review-reply">
                        <p className="text-xs font-semibold text-gold-700">Response from MCC Service Corp.</p>
                        <p className="text-sm text-slate-600 mt-0.5">{data.review.reply}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-heading font-semibold text-slate-900 mb-2">How did we do?</p>
                    <div className="flex gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)} className="transition-transform hover:scale-110">
                          <StarIcon size={30} weight={n <= rating ? "fill" : "regular"} className="text-gold-500" />
                        </button>
                      ))}
                    </div>
                    <Textarea data-testid="review-comment" value={reviewComment} onChange={(e) => setReviewComment(e.target.value)} placeholder="Tell us about your experience (optional)" className="bg-white border-slate-300" />
                    <button data-testid="submit-review-btn" onClick={submitReview} disabled={!rating || reviewLoading} className="mt-3 rounded-sm bg-gold-600 hover:bg-gold-700 text-white font-semibold px-6 py-2.5 text-sm transition-colors active:scale-95 disabled:opacity-50">
                      {reviewLoading ? "Submitting…" : "Submit review"}
                    </button>
                  </>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {lightbox && (
        <div
          data-testid="gallery-lightbox"
          className="fixed inset-0 z-[100] bg-navy-900/95 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
          onClick={() => setLightbox(null)}
        >
          <button
            data-testid="lightbox-close"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 text-white/70 hover:text-white transition-colors"
          >
            <XIcon size={30} weight="bold" />
          </button>
          {lightbox.photos.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length })); }}
                className="absolute left-3 sm:left-8 text-white/60 hover:text-white transition-colors p-2"
              >
                <CaretLeftIcon size={40} weight="bold" />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); setLightbox((lb) => ({ ...lb, index: (lb.index + 1) % lb.photos.length })); }}
                className="absolute right-3 sm:right-8 text-white/60 hover:text-white transition-colors p-2"
              >
                <CaretRightIcon size={40} weight="bold" />
              </button>
            </>
          )}
          <div className="max-w-4xl max-h-[85vh] px-4 flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={`${API}/files/${lightbox.photos[lightbox.index]}`}
              alt="finished work"
              className="max-h-[78vh] w-auto object-contain rounded-sm shadow-2xl"
            />
            <p className="text-white/70 text-xs mt-4 tracking-wide">{lightbox.index + 1} / {lightbox.photos.length}</p>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
